import { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { Box, Typography, Paper } from '@mui/material'
import { Lock } from '@mui/icons-material'
import { usePermissions, type PermissionKey, type RoleName } from '../hooks/usePermissions'

interface RoleGuardProps {
  children: ReactNode
  /** User must have at least one of these roles. */
  roles?: RoleName[]
  /** User must have at least one of these permissions. */
  permissions?: PermissionKey[]
  /** What to show when access is denied. Defaults to a friendly forbidden card. */
  fallback?: ReactNode
  /** When set, redirect to this path on denial instead of showing fallback. */
  redirectTo?: string
}

/**
 * RoleGuard hides children unless the current user has the required role
 * or permission. Useful for both pages and inline UI bits.
 */
export function RoleGuard({
  children,
  roles,
  permissions,
  fallback,
  redirectTo,
}: RoleGuardProps) {
  const { hasRole, hasPermission } = usePermissions()

  const roleOk = !roles || roles.length === 0 || hasRole(...roles)
  const permOk = !permissions || permissions.length === 0 || hasPermission(...permissions)
  const allowed = roleOk && permOk

  if (allowed) return <>{children}</>
  if (redirectTo) return <Navigate to={redirectTo} replace />
  if (fallback !== undefined) return <>{fallback}</>

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4, maxWidth: 420, textAlign: 'center',
          border: '1px solid #e2e8f0', borderRadius: 2,
        }}
      >
        <Lock sx={{ fontSize: 40, color: '#94a3b8', mb: 1 }} />
        <Typography variant="h6" fontWeight={700} color="#0f172a" gutterBottom>
          Access denied
        </Typography>
        <Typography variant="body2" color="#64748b">
          You don't have permission to view this page. Contact your tenant administrator
          if you believe this is a mistake.
        </Typography>
      </Paper>
    </Box>
  )
}
